/**
 * Matter Display Utilities
 *
 * Utility functions for displaying matter-related information
 * with graceful fallbacks for legacy data.
 */

/**
 * Contact-like object with name fields
 */
interface ContactLike {
  firstName?: string | null;
  lastName?: string | null;
  organisationName?: string | null;
}

/**
 * Matter-like object for display name extraction
 */
interface MatterLike {
  type?: string;
  legalPersonalRepresentatives?: Array<
    ContactLike & { isLegalPersonalRepresentative?: boolean }
  >;
  clients?: ContactLike[];
  metadata?: {
    clientNames?: string[];
  };
}

/**
 * Whether a matter's type slug is estate-administration.
 *
 * The single, case-insensitive estate-administration predicate — reuse this
 * everywhere the estate/non-estate fork is decided (matter-type-driven shell,
 * LF-5436) rather than re-inlining `type === 'estate-administration'`.
 */
export function isEstateAdminType(type?: string | null): boolean {
  return type?.toLowerCase() === 'estate-administration';
}

/**
 * Get display name from a contact-like object
 */
export function getContactDisplayName(contact: ContactLike): string {
  if (contact.organisationName) {
    return contact.organisationName;
  }
  return [contact.firstName, contact.lastName]
    .filter((part): part is string => Boolean(part))
    .join(' ')
    .trim();
}

/**
 * Get display names for matter contacts based on matter type.
 *
 * - Estate-administration matters: Returns LPR names
 * - Non-estate-administration matters: Returns clients (with fallback to clientNames)
 *
 * This implements a graceful migration from `metadata.clientNames` (legacy string array)
 * to `clients` (real Contact objects) for non-estate-administration matters.
 *
 * @param matter - The matter object
 * @returns Array of display names for the matter's contacts
 */
export function getMatterContactDisplayNames(matter: MatterLike): string[] {
  const isEstateAdministration = isEstateAdminType(matter.type);

  if (isEstateAdministration) {
    // Estate-administration: Display LPR names
    const lprs = matter.legalPersonalRepresentatives || [];
    const filteredLprs = lprs.filter(
      (lpr) => lpr.isLegalPersonalRepresentative
    );
    return filteredLprs
      .map((lpr) => getContactDisplayName(lpr))
      .filter((name) => name.length > 0);
  }

  // Non-estate-administration matters:
  // Prefer clients array, fall back to legacy clientNames
  if (matter.clients && matter.clients.length > 0) {
    return matter.clients
      .map((client) => getContactDisplayName(client))
      .filter((name) => name.length > 0);
  }

  // Fallback to legacy metadata.clientNames
  return matter.metadata?.clientNames || [];
}

/**
 * Matter-like object for title formatting.
 * Uses structural typing so it works with both TaskMatter and Matter.
 */
interface MatterTitleLike {
  type?: string;
  typeLabel?: string;
  decedentFirstName?: string | null;
  decedentMiddleName?: string | null;
  decedentLastName?: string | null;
  metadata?: {
    description?: string;
    clientNames?: string[];
  };
  clients?: Array<{
    id?: number;
    firstName?: string;
    middleName?: string | null;
    lastName?: string;
    organisationName?: string | null;
  }>;
}

type TitleClient = NonNullable<MatterTitleLike['clients']>[number];

/**
 * Two people are named the way a solicitor names their file: "Margaret and
 * Geoffrey Wilson" when they share a surname, "Margaret Wilson and Geoffrey
 * Hale" when they do not. Null for anything else (one client, three, or an
 * organisation), which keeps the list form.
 */
function formatCoupleTitle(clients: TitleClient[]): string | null {
  if (clients.length !== 2) return null;
  const [a, b] = clients;
  if (a.organisationName || b.organisationName) return null;
  if (!a.firstName || !b.firstName || !a.lastName || !b.lastName) return null;
  if (a.lastName === b.lastName) {
    return `${a.firstName} and ${b.firstName} ${a.lastName}`;
  }
  return `${a.firstName} ${a.lastName} and ${b.firstName} ${b.lastName}`;
}

/**
 * Format a matter's display title based on its type and available data.
 *
 * Priority:
 * 1. Business-law → metadata.description
 * 2. Estate-administration → "Estate of the late {decedent}"
 * 3. Clients array (new contact-based)
 * 4. Legacy metadata.clientNames
 * 5. Decedent name fallback (for non-estate matters with decedent fields)
 * 6. typeLabel or "Unnamed Matter"
 */
export function formatMatterTitle(
  matter: MatterTitleLike,
  fallbackLabel = 'Unnamed Matter'
): string {
  // Business Law matters use description as title
  if (
    matter.type?.toLowerCase() === 'business-law' &&
    matter.metadata?.description
  ) {
    return matter.metadata.description;
  }

  // Estate-administration matters: always prefer decedent name over client names
  // A contact can be both isClient and isLegalPersonalRepresentative,
  // so we must check decedent fields first for estate matters
  const isEstateAdmin = isEstateAdminType(matter.type);
  if (isEstateAdmin) {
    const parts = [
      matter.decedentFirstName,
      matter.decedentMiddleName,
      matter.decedentLastName
    ].filter(Boolean);
    if (parts.length > 0) {
      return `Estate of the late ${parts.join(' ')}`;
    }
  }

  // Non-estate matter with clients (new contact-based) - prefer over legacy clientNames
  if (matter.clients && matter.clients.length > 0) {
    const couple = formatCoupleTitle(matter.clients);
    if (couple) return couple;
    return matter.clients
      .map(
        (c) =>
          c.organisationName ||
          [c.firstName, c.middleName, c.lastName].filter(Boolean).join(' ') ||
          (c.id ? `Client #${c.id}` : 'Unknown Client')
      )
      .join(', ');
  }

  // Non-estate matter with client names (legacy fallback)
  if (matter.metadata?.clientNames && matter.metadata.clientNames.length > 0) {
    return matter.metadata.clientNames.join(', ');
  }

  // Estate matter without decedent fields: decedent-name fallback. Gated on
  // estate-administration so a non-estate matter that happens to carry stray
  // decedent fields never leaks an "Estate of the late …" title (LF-5436).
  if (isEstateAdmin) {
    const parts = [
      matter.decedentFirstName,
      matter.decedentMiddleName,
      matter.decedentLastName
    ].filter(Boolean);
    if (parts.length > 0) {
      return `Estate of the late ${parts.join(' ')}`;
    }
  }

  // Fallback
  return matter.typeLabel || fallbackLabel;
}

/**
 * Solicitor/assignee-like object with nullable name fields (a `law_firm_users`
 * row — LF-6015/LF-6056).
 */
interface SolicitorLike {
  firstName?: string | null;
  lastName?: string | null;
}

/**
 * Get the name to show for a matter's assigned solicitor: the matter's own
 * acting solicitor, falling back to the task's assignee. Blank when neither
 * has a name — both shapes lack an `email` to fall back to here, so an
 * empty result is the honest answer rather than a fabricated one.
 *
 * Was duplicated byte-for-byte between the compact and draggable matter-list
 * rows; consolidated in LF-6056.
 */
export function getAssignedSolicitorName(
  actingSolicitor: SolicitorLike | null | undefined,
  taskAssignee: SolicitorLike | null | undefined
): string | undefined {
  const solicitor = actingSolicitor || taskAssignee;
  if (!solicitor) return undefined;
  return (
    `${solicitor.firstName || ''} ${solicitor.lastName || ''}`.trim() ||
    undefined
  );
}

/**
 * Format contact names for display with truncation
 *
 * Shows first N names, then "+X more" for remaining
 *
 * @param names - Array of names to format
 * @param maxVisible - Maximum names to show before truncating (default: 2)
 * @returns Formatted string like "John Smith, Jane Doe, +3 more"
 */
export function formatContactNamesForDisplay(
  names: string[],
  maxVisible: number = 2
): string | undefined {
  if (names.length === 0) {
    return undefined;
  }

  if (names.length <= maxVisible) {
    return names.join(', ');
  }

  const visibleNames = names.slice(0, maxVisible).join(', ');
  const remainingCount = names.length - maxVisible;
  return `${visibleNames}, +${remainingCount} more`;
}
